import { useEntityReferenceDirectories } from "@/hooks/use-entity-reference-directories.hook.ts";
import { InformBox } from "@/components/inform-box/InformBox.tsx";
import { DeleteListItem } from "@/components/list-item/DeleteListItem.tsx";
import {
  ConfirmationModal,
  useModals,
  useToasts,
  Modal,
  ModalProps,
  Button,
  ButtonLink,
  FontAwesomeIcon,
  Input,
  Select,
  SelectItem,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@skailer-com/uikit";
import useCustomFieldsHook from "@/hooks/use-custom-fields.hook.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import usePermissions from "@/hooks/use-permissions.hook.ts";
import { CustomFieldOptions } from "@/pages/settings/custom-fields/components/CustomFieldOptions.tsx";
import { CustomFieldChoiceTypeStep } from "@/pages/settings/custom-fields/components/CustomFieldChoiceTypeStep.tsx";
import { EmployeeCustomFieldDefaultValue } from "@/pages/settings/custom-fields/widgets/EmployeeCustomFieldDefaultValue.tsx";
import {
  useFieldDelete,
  useFieldSave,
  useFieldsCheckSlug,
  useSectionsByType,
  useTabsByEntityType,
} from "@/queries/use-custom-fields.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiField,
  ApiFieldValueTypes,
  ApiFieldReferenceEntity,
  ApiCFEntityTypes,
  ApiFieldTypes,
  CustomFieldOptionPayload,
  ApiSectionTypes,
} from "@/types/custom-field.types.ts";
import { ApiPermission } from "@/types/role.types.ts";
import { ApiFeatureType } from "@/types/subscription.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

enum CustomFieldCreateSteps {
  choiceType = "choiceType",
  addData = "addData",
}

enum FieldVisibility {
  never = "never",
  whenFilled = "when_filled",
  always = "always",
}

interface CustomFieldEditModalProps extends ModalProps {
  field?: ApiField;
  tabId?: number;
  entityType?: ApiCFEntityTypes;
}

export function CustomFieldEditModal({
  field,
  tabId,
  entityType = field?.entity_type ?? ApiCFEntityTypes.employee,
  ...props
}: CustomFieldEditModalProps) {
  const { id } = props;
  const isEdit = !!field;
  const systemMandatory = field?.is_system_mandatory ?? false;
  const isSystemField = Boolean(field?.is_system);
  const isViewOnlyField = field?.type === ApiFieldTypes.applicant_additional_contacts;
  const { close, openModal } = useModals();
  const { t } = useTranslation();
  const referenceDirectories = useEntityReferenceDirectories();
  const { showInfo } = useToasts();
  const { getFieldTypeInformation, fieldTypeInfo, getFieldTitle, getSectionTitle, getTabTitle } =
    useCustomFieldsHook();
  const localizedSystemFieldTitle =
    isSystemField && field ? getFieldTitle({ type: field.type, title: "" }).trim() : "";
  const [step, setStep] = useState<CustomFieldCreateSteps>(
    isEdit ? CustomFieldCreateSteps.addData : CustomFieldCreateSteps.choiceType
  );
  const [currentOptions, setCurrentOptions] = useState<CustomFieldOptionPayload[]>(
    ...[field?.options ?? []]
  );
  const currentOptionsRef = useRef<CustomFieldOptionPayload[]>(currentOptions);
  const [checkedTitle, setCheckedTitle] = useState<string>("");
  const [optionsError, setOptionsError] = useState<string>("");
  const availableFieldTypeInfo =
    entityType === ApiCFEntityTypes.applicant
      ? Object.fromEntries(
          Object.entries(fieldTypeInfo).filter(
            ([type]) =>
              type !== ApiFieldValueTypes.employee_reference &&
              type !== ApiFieldValueTypes.entity_reference
          )
        )
      : fieldTypeInfo;
  const [chosenType, setChosenType] = useState<ApiFieldValueTypes | undefined>(field?.field_type);
  const { data: sections } = useSectionsByType(entityType);
  const { data: tabs } = useTabsByEntityType(entityType);
  const currentTabId = useMemo(() => {
    if (tabId) return tabId;
    if (field) return sections?.find((s) => s.id === field.section_id)?.tab_id;
    return undefined;
  }, [tabId, field, sections]);
  const sectionOptions = useMemo(() => {
    const filtered =
      sections?.filter(
        (section) =>
          ![
            ApiSectionTypes.employee_overview_summary,
            ApiSectionTypes.employee_overview_teams,
            ApiSectionTypes.employee_overview_direct_reports,
            ApiSectionTypes.employee_overview_department_employees,
            ApiSectionTypes.employee_overview_recognitions,
            ApiSectionTypes.employee_basic_info_recognitions,
            ApiSectionTypes.employee_basic_info_hired_via_job,
          ].includes(section.section_type)
      ) ?? [];

    const tabOrder = new Map((tabs ?? []).map((tab, index) => [tab.id, index]));
    const tabTitleById = new Map((tabs ?? []).map((tab) => [tab.id, getTabTitle(tab)]));

    return filtered
      .slice()
      .sort((a, b) => {
        if (currentTabId !== undefined) {
          if (a.tab_id === currentTabId && b.tab_id !== currentTabId) return -1;
          if (b.tab_id === currentTabId && a.tab_id !== currentTabId) return 1;
        }
        const aIdx = tabOrder.get(a.tab_id) ?? Number.MAX_SAFE_INTEGER;
        const bIdx = tabOrder.get(b.tab_id) ?? Number.MAX_SAFE_INTEGER;
        return aIdx - bIdx;
      })
      .map(
        (section) =>
          ({
            id: section.id,
            title: getSectionTitle(section),
            group: tabTitleById.get(section.tab_id) ?? "",
          }) as SelectItem
      );
  }, [sections, tabs, currentTabId, getSectionTitle, getTabTitle]);
  const CustomSectionSchemaObject = z.object({
    title: z
      .string({ error: t("errors:field_required") })
      .trim()
      .min(1, t("errors:field_required")),
    section_id: z.number({ error: t("errors:field_required") }),
    description: z.string().optional(),
    hint: z.string().optional(),
    is_mandatory: z.boolean().optional(),
    visibility: z.enum(FieldVisibility),
    is_multiple: z.boolean().optional(),
    is_visible_to_candidate: z.boolean().optional(),
    reference_entity: z.enum(ApiFieldReferenceEntity).optional(),
  });

  const { canIEdit, isFeatureEnabled } = usePermissions();

  const {
    handleSubmit,
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(CustomSectionSchemaObject),
    defaultValues: {
      title: field?.title?.trim() || localizedSystemFieldTitle,
      description: field?.description ?? "",
      section_id: field?.section_id,
      is_mandatory: field?.is_mandatory ?? false,
      visibility: field?.is_hidden
        ? FieldVisibility.never
        : !isSystemField && field?.is_always_visible
          ? FieldVisibility.always
          : FieldVisibility.whenFilled,
      is_multiple: field?.config?.is_multiple_values ?? false,
      is_visible_to_candidate: field?.config?.is_visible_to_candidate ?? false,
      reference_entity: field?.reference_entity ?? ApiFieldReferenceEntity.legal_entity,
      hint: field?.hint ?? "",
    },
  });
  const fieldTitle = watch("title");
  const isLocalizedSystemFieldTitle =
    isSystemField && fieldTitle?.trim() === localizedSystemFieldTitle;
  const showSystemFieldTitleReset =
    isSystemField && Boolean(localizedSystemFieldTitle) && !isLocalizedSystemFieldTitle;
  const visibility = watch("visibility");
  const { mutateAsync: save, isPending: isSavePending } = useFieldSave(control);
  const { data: checkResult } = useFieldsCheckSlug(checkedTitle, entityType, field?.id);
  const { mutateAsync: fieldDelete } = useFieldDelete();
  const slugIsOk = checkResult?.success ?? true;

  const chosenTypeItem = useMemo(() => {
    return chosenType ? getFieldTypeInformation(chosenType, field?.type) : undefined;
  }, [chosenType, field?.type, getFieldTypeInformation]);

  const title =
    step == CustomFieldCreateSteps.choiceType
      ? t("custom_fields:choose_field_type")
      : !field
        ? t("custom_fields:create_new_field_type", {
            field_type: chosenTypeItem?.title.toLowerCase(),
          })
        : t("custom_fields:edit_field_type", {
            field_type: chosenTypeItem?.title.toLowerCase(),
          });

  useEffect(() => {
    currentOptionsRef.current = currentOptions;

    if (currentOptions.length > 0) {
      setOptionsError("");
    }
  }, [currentOptions]);

  const onSubmit = handleSubmit(async (data) => {
    if (!chosenType) return;

    if (isSavePending || !slugIsOk) return;

    if (
      !isSystemField &&
      chosenType === ApiFieldValueTypes.select &&
      currentOptionsRef.current.length === 0
    ) {
      setOptionsError(t("custom_fields:options_required_for_select"));
      return;
    }

    setOptionsError("");
    const isMandatory =
      systemMandatory ||
      (data.visibility !== FieldVisibility.never && (data.is_mandatory ?? false));
    const systemFieldTitlePayload =
      data.title === localizedSystemFieldTitle
        ? field?.title?.trim()
          ? { title: null }
          : {}
        : { title: data.title };

    const fieldSaved = await save(
      isSystemField
        ? {
            id: field!.id,
            payload: {
              ...systemFieldTitlePayload,
              description: data.description || null,
              hint: data.hint || null,
              is_mandatory: isMandatory,
              is_hidden: !systemMandatory && data.visibility === FieldVisibility.never,
            },
          }
        : {
            id: field?.id,
            payload: {
              field_type: chosenType,
              title: data.title,
              description: data.description || null,
              hint: data.hint || null,
              is_mandatory: isMandatory,
              is_hidden: !systemMandatory && data.visibility === FieldVisibility.never,
              is_always_visible: data.visibility === FieldVisibility.always,
              section_id: data.section_id,
              config: {
                is_multiple_values: data.is_multiple ?? false,
                is_visible_to_candidate:
                  entityType === ApiCFEntityTypes.offer
                    ? (data.is_visible_to_candidate ?? false)
                    : undefined,
              },
              reference_entity:
                !isEdit && chosenType === ApiFieldValueTypes.entity_reference
                  ? data.reference_entity
                  : undefined,
              options: currentOptionsRef.current,
            },
          }
    );

    if (fieldSaved) {
      if (!isSystemField && entityType === ApiCFEntityTypes.employee) {
        openModal(EmployeeCustomFieldDefaultValue, { field: fieldSaved, fromEdit: !!field });
      }

      if (field) {
        analyticsService.trackEvent(analyticEvents.customFields.fieldEdited, {
          [analyticProperties.id]: fieldSaved.id,
        });
      } else {
        analyticsService.trackEvent(analyticEvents.customFields.fieldCreated, {
          [analyticProperties.id]: fieldSaved.id,
        });
      }

      close(id);
    }
  });

  const onNext = useCallback(async () => {
    if (step == CustomFieldCreateSteps.choiceType) {
      setStep(CustomFieldCreateSteps.addData);
    } else {
      await onSubmit();
    }
  }, [step, onSubmit]);

  const onBack = useCallback(() => {
    if (step == CustomFieldCreateSteps.choiceType || isEdit) return;

    return () => {
      setStep(CustomFieldCreateSteps.choiceType);
    };
  }, [step, isEdit]);

  useDebounceUpdate({
    data: isLocalizedSystemFieldTitle ? "" : fieldTitle,
    timeout: 300,
    execute: setCheckedTitle,
  });

  const isTypeWithOptions = useMemo(() => {
    if (!chosenType) return false;

    return [ApiFieldValueTypes.select].includes(chosenType);
  }, [chosenType]);

  const handleDelete = () => {
    if (!field) return;

    openModal(ConfirmationModal, {
      question: t("common:attention"),
      text: t("common:delete_confirm"),
      acceptTitle: t("common:yes"),
      onAccept: async function () {
        try {
          const result = await fieldDelete(field.id);

          if (result) showInfo({ title: t("custom_fields:field_deleted") });

          analyticsService.trackEvent(analyticEvents.customFields.fieldDeleted, {
            [analyticProperties.id]: field.id,
          });
        } catch {
          return;
        }

        close(id);
      },
    });
  };

  return (
    <Modal
      {...props}
      title={title}
      onBack={onBack()}
      withCloser={true}
      layout={"stretch"}
      actions={
        <Stack gap={"md"}>
          {fieldTitle?.length > 0 && !slugIsOk ? (
            <Stack className={"h-14"} justify={"center"}>
              <Text className={"text-danger"}>{t("custom_fields:change_field_name")}</Text>
            </Stack>
          ) : (
            <></>
          )}
          <ServerErrorField errors={errors} />
          {optionsError && (
            <Stack className={"h-14"} justify={"center"}>
              <Text className={"text-danger"}>{optionsError}</Text>
            </Stack>
          )}

          <Button size={"lg"} variant={"primary"} onClick={onNext} isLoading={isSavePending}>
            {step == CustomFieldCreateSteps.addData ? t("common:save") : t("common:next")}
          </Button>
        </Stack>
      }
    >
      {step == CustomFieldCreateSteps.choiceType ? (
        <CustomFieldChoiceTypeStep
          fieldTypeInfo={availableFieldTypeInfo}
          value={chosenType}
          onChange={setChosenType}
        />
      ) : (
        <></>
      )}
      {step == CustomFieldCreateSteps.addData ? (
        <form onSubmit={onSubmit}>
          <Stack gap={"md"}>
            <Stack>
              <Controller
                control={control}
                name="title"
                render={({ field: { value, onChange, onBlur, ref } }) => (
                  <Input
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                    label={t("common:title")}
                    error={errors.title?.message}
                    required
                  />
                )}
              />
              {showSystemFieldTitleReset && (
                <Stack direction="horizontal" items="center" gap="xs" className="py-2">
                  <Text className="text-sm text-secondary">
                    {t("custom_fields:system_field_title_reset_hint")}
                  </Text>
                  <button
                    type="button"
                    className="cursor-pointer text-sm text-brand-dark underline hover:no-underline"
                    onClick={() => {
                      setValue("title", localizedSystemFieldTitle, {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                      setCheckedTitle("");
                    }}
                  >
                    {t("common:reset")}
                  </button>
                </Stack>
              )}
              {!isSystemField && (
                <>
                  <Controller
                    render={({ field: { onChange, value } }) => (
                      <Select
                        emptyTitle={t("common:section")}
                        onChange={onChange}
                        value={value}
                        options={sectionOptions}
                        required
                        error={errors.section_id?.message}
                      />
                    )}
                    name={"section_id"}
                    control={control}
                  />
                </>
              )}
              {chosenType &&
                !isViewOnlyField &&
                [
                  ApiFieldValueTypes.text,
                  ApiFieldValueTypes.textarea,
                  ApiFieldValueTypes.phone,
                  ApiFieldValueTypes.number,
                  ApiFieldValueTypes.percentage,
                  ApiFieldValueTypes.email,
                  ApiFieldValueTypes.url,
                  ApiFieldValueTypes.select,
                  ApiFieldValueTypes.text_array,
                  ApiFieldValueTypes.employee_reference,
                  ApiFieldValueTypes.entity_reference,
                ].includes(chosenType) && (
                  <Input
                    label={t("custom_fields:placeholder")}
                    {...register("description")}
                    error={errors.description?.message}
                  />
                )}
              <Textarea
                label={t("custom_fields:hint")}
                {...register("hint")}
                error={errors.hint?.message}
              />
              <Controller
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t("custom_fields:visibility")}
                    emptyTitle={t("common:select")}
                    value={value}
                    options={[
                      {
                        id: FieldVisibility.never,
                        title: t("custom_fields:visibility_never"),
                      },
                      {
                        id: FieldVisibility.whenFilled,
                        title: t("custom_fields:visibility_when_filled"),
                      },
                      ...(!isSystemField
                        ? [
                            {
                              id: FieldVisibility.always,
                              title: t("custom_fields:visibility_always"),
                            },
                          ]
                        : []),
                    ]}
                    onChange={(nextVisibility) => {
                      onChange(nextVisibility);
                      if (nextVisibility === FieldVisibility.never) {
                        setValue("is_mandatory", false);
                      }
                    }}
                    disabled={systemMandatory}
                  />
                )}
                name={"visibility"}
                control={control}
              />
            </Stack>
            {field?.type === ApiFieldTypes.job_description &&
              visibility === FieldVisibility.never &&
              isFeatureEnabled(ApiFeatureType.ats_ai) && (
                <InformBox
                  title={t("custom_fields:job_description_hidden_ai_title")}
                  description={t("custom_fields:job_description_hidden_ai_description")}
                />
              )}

            {!isViewOnlyField && (
              <Controller
                render={({ field: { onChange, value } }) => (
                  <Switch
                    onChange={onChange}
                    checked={value}
                    disabled={visibility === FieldVisibility.never || systemMandatory}
                    title={t("custom_fields:mandatory")}
                    description={t(
                      entityType === ApiCFEntityTypes.job
                        ? "custom_fields:make_mandatory_job"
                        : entityType === ApiCFEntityTypes.offer
                          ? "custom_fields:make_mandatory_offer"
                          : "custom_fields:make_mandatory"
                    )}
                  />
                )}
                name={"is_mandatory"}
                control={control}
              />
            )}
            {!isSystemField && entityType === ApiCFEntityTypes.offer && (
              <Controller
                render={({ field: { onChange, value } }) => (
                  <Switch
                    onChange={onChange}
                    checked={value}
                    disabled={visibility === FieldVisibility.never}
                    title={t("custom_fields:visible_to_candidate")}
                    description={t("custom_fields:visible_to_candidate_description")}
                  />
                )}
                name={"is_visible_to_candidate"}
                control={control}
              />
            )}
            {!isSystemField &&
              (chosenType === ApiFieldValueTypes.select ||
                chosenType === ApiFieldValueTypes.employee_reference ||
                chosenType === ApiFieldValueTypes.entity_reference) && (
                <Controller
                  render={({ field: { onChange, value } }) => (
                    <Switch
                      onChange={onChange}
                      checked={value}
                      title={t("custom_fields:multiple")}
                      description={t(
                        chosenType === ApiFieldValueTypes.employee_reference
                          ? "custom_fields:multiple_employees_description"
                          : chosenType === ApiFieldValueTypes.entity_reference
                            ? "custom_fields:multiple_references_description"
                            : "custom_fields:multiple_description"
                      )}
                    />
                  )}
                  name={"is_multiple"}
                  control={control}
                />
              )}
            {!isSystemField && chosenType === ApiFieldValueTypes.entity_reference && (
              <Controller
                render={({ field: { onChange, value } }) => (
                  <Select
                    label={t("custom_fields:reference_directory")}
                    emptyTitle={t("common:select")}
                    value={value}
                    options={referenceDirectories}
                    onChange={onChange}
                    required
                    disabled={isEdit}
                  />
                )}
                name={"reference_entity"}
                control={control}
              />
            )}
            {!isSystemField && isTypeWithOptions && (
              <CustomFieldOptions
                highlightWarning={!!optionsError}
                options={currentOptions}
                updateOptions={setCurrentOptions}
              />
            )}
            {!isEdit && entityType === ApiCFEntityTypes.employee && (
              <InformBox
                emoji={":bulb:"}
                title={t("custom_fields:just_reminder")}
                description={t("custom_fields:information_about_permissions", {
                  this_entity: t("custom_fields:this_field"),
                  entity_be_visible: t("custom_fields:field_be_visible"),
                })}
                actionsSlot={
                  isEdit && canIEdit(ApiPermission.settings_roles_permissions) ? (
                    <Stack items={"start"}>
                      <ButtonLink
                        href="/settings/roles-and-permissions"
                        target={"_blank"}
                        size={"sm"}
                        variant={"secondary"}
                        rightIcon={
                          <FontAwesomeIcon icon="fa-light fa-arrow-up-right-from-square" />
                        }
                      >
                        {t("custom_fields:open_role_permissions")}
                      </ButtonLink>
                    </Stack>
                  ) : (
                    <></>
                  )
                }
              />
            )}
            {field && !isSystemField ? (
              <DeleteListItem
                onDelete={handleDelete}
                title={t("custom_fields:confirmation_delete_field")}
              />
            ) : (
              <></>
            )}
          </Stack>
        </form>
      ) : (
        <></>
      )}
    </Modal>
  );
}
